
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSession } from "@/hooks/useSession";
import { logger } from "@/lib/logger";
import { useToast } from "@/hooks/use-toast"; 
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, hasSessionError, refreshSession } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [isChecking, setIsChecking] = useState(true);
  const [redirectAttempted, setRedirectAttempted] = useState(false);

  useEffect(() => {
    // Handle session errors by trying to refresh
    if (hasSessionError && !redirectAttempted) {
      logger.warn("Session has errors, attempting refresh");
      refreshSession();
    }
  }, [hasSessionError, refreshSession, redirectAttempted]);

  useEffect(() => {
    if (!isLoading) {
      // Only redirect if not loading and not authenticated
      if (!isAuthenticated && !redirectAttempted) {
        logger.info({
          message: "User not authenticated, redirecting to login page",
          context: { path: location.pathname }
        });
        
        setRedirectAttempted(true);
        
        toast({
          title: "Authentication required",
          description: "Please log in to access this area",
          variant: "destructive",
        });
        
        const returnUrl = encodeURIComponent(location.pathname + location.search);
        navigate(`/login?redirect=${returnUrl}`, { replace: true });
      }
      
      // Mark checking as complete
      setIsChecking(false);
    }
  }, [isLoading, isAuthenticated, redirectAttempted, navigate, location.pathname, location.search, toast]);

  // Show loading state when either the session is loading or we're still checking
  if (isLoading || isChecking) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <span className="text-lg">Loading your dashboard...</span>
      </div>
    );
  }

  // If there's no session after loading completes, return null (redirect handled in useEffect)
  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
